import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import {
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  MapPinIcon,
  XMarkIcon,
  BuildingStorefrontIcon,
} from '@heroicons/react/24/outline';

import DatePicker from 'components/Datepicker';
import FiltersModal from 'components/FiltersModal';
import CategoryIcon from 'components/CategoryIcon';
import ScrollingPlaceholder from 'components/ScrollingPlaceholder';

import { fetchSuggestions } from '../../redux/actions/suggestions';
import getCategorySuggestions, {
  getIdsFromQuery,
} from '../../utils/searchSuggestions';
import { categories } from '../../utils/categories';
import { UTILITIES } from '../../utils/utilities';
import { cx, useRotatingText } from './hooks';

const EMPTY_LOCATION = {
  option: null,
  label: '',
  primary: '',
  city: '',
  country: '',
  countryCode: '',
  placeId: '',
};

const isCityOrCountry = (types = []) =>
  Array.isArray(types) &&
  (types.includes('country') ||
    types.includes('locality') ||
    types.includes('postal_town') ||
    types.includes('administrative_area_level_3') ||
    types.includes('administrative_area_level_2'));

const isCountryPrediction = (opt) => {
  const types = opt?.data?.value?.types || [];
  const terms = opt?.data?.value?.terms || [];
  return (
    (Array.isArray(types) && types.includes('country')) ||
    (Array.isArray(terms) && terms.length === 1)
  );
};

const toYmd = (v) => (v ? String(v).slice(0, 10) : '');

// 16px on mobile: iOS Safari auto-zooms (and stays zoomed) when a focused
// input's font size is under 16px.
const responsiveFontSize = {
  fontSize: '16px',
  '@media (min-width: 640px)': { fontSize: '14px' },
};

// Hero search (T2B-350 §5/§8): keyword input with live brand/category
// suggestions, while When & Where live inside the Filters panel.
export default function HeroSearch({ tab, setTab }) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const wrapperRef = useRef(null);
  const justSelectedSuggestion = useRef(false);

  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [location, setLocation] = useState(EMPTY_LOCATION);
  const [selectedDates, setSelectedDates] = useState({ from: '', to: '' });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    macroCategories: [],
    microCategories: [],
    utilities: [],
    maxPrice: null,
    hidePastEvents: true,
    hideLiveEvents: false,
    monthRange: { from: '', to: '' },
  });

  const isBrand = tab === 'brands';

  // Rotating search placeholder (T2B-390): cycle through a set of example
  // prompts while the field is empty. Pauses once the user starts typing.
  const placeholderList = t(
    isBrand
      ? 'homepage.search_brands_placeholders'
      : 'homepage.search_events_placeholders',
    { returnObjects: true }
  );
  const rotatingPlaceholder = useRotatingText(
    Array.isArray(placeholderList) ? placeholderList : [],
    { enabled: !query }
  );
  const basePlaceholder = isBrand
    ? t('homepage.search_brands_placeholder')
    : t('homepage.search_events_placeholder');
  const activePlaceholder = rotatingPlaceholder || basePlaceholder;

  // Reset location when switching between events/brands (different geo rules).
  useEffect(() => {
    setLocation(EMPTY_LOCATION);
    if (isBrand) setSelectedDates({ from: '', to: '' });
  }, [tab, isBrand]);

  // Live suggestions as the user types (categories always; brand names too).
  useEffect(() => {
    if (justSelectedSuggestion.current) {
      justSelectedSuggestion.current = false;
      return;
    }
    const q = (query || '').trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }

    let alive = true;
    const timer = setTimeout(() => {
      if (!alive) return;
      if (isBrand) {
        dispatch(fetchSuggestions(q, 'brands'))
          .then((backend) => {
            if (!alive) return;
            const seen = new Set();
            const merged = [...getCategorySuggestions(q), ...backend].filter(
              (s) => {
                const k = `${s.type || 'x'}:${(s.name || '').toLowerCase()}`;
                if (seen.has(k)) return false;
                seen.add(k);
                return true;
              }
            );
            setSuggestions(merged);
          })
          .catch(() => alive && setSuggestions([]));
      } else {
        setSuggestions(getCategorySuggestions(q));
      }
    }, 200);

    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [query, isBrand, dispatch]);

  // Close suggestions / filters panel on outside click.
  useEffect(() => {
    const onDown = (e) => {
      if (wrapperRef.current?.contains(e.target)) return;
      setSuggestions([]);
      setFiltersOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const gpaStyles = useMemo(
    () => ({
      container: (p) => ({ ...p, width: '100%' }),
      control: (p) => ({
        ...p,
        backgroundColor: 'transparent',
        border: 'none',
        boxShadow: 'none',
        cursor: 'text',
        minHeight: '40px',
        paddingLeft: '34px',
        ...responsiveFontSize,
      }),
      valueContainer: (p) => ({ ...p, padding: 0 }),
      input: (p) => ({
        ...p,
        margin: 0,
        padding: 0,
        color: '#1F2937',
        ...responsiveFontSize,
      }),
      singleValue: (p) => ({
        ...p,
        color: '#1F2937',
        ...responsiveFontSize,
      }),
      placeholder: (p) => ({
        ...p,
        color: '#9BA3AF',
        ...responsiveFontSize,
      }),
      dropdownIndicator: () => ({ display: 'none' }),
      indicatorSeparator: () => ({ display: 'none' }),
      // The menu renders inline (not portaled) so it stays glued to the
      // input when the page scrolls — a fixed/portaled menu keeps its
      // open-time coordinates and drifts away from the field.
      menu: (p) => ({
        ...p,
        borderRadius: '12px',
        overflow: 'hidden',
        zIndex: 50,
      }),
      option: (p, state) => ({
        ...p,
        fontSize: '14px',
        backgroundColor: state.isFocused ? '#F3F4F6' : 'white',
        color: '#111827',
      }),
    }),
    []
  );

  const onPlaceChange = async (place) => {
    if (!place) {
      setLocation(EMPTY_LOCATION);
      return;
    }
    const predTypes = place?.value?.types || [];
    if (!isBrand && !isCityOrCountry(predTypes)) {
      setLocation(EMPTY_LOCATION);
      return;
    }

    const label = String(place.label || '').trim();
    const placeId = place?.value?.place_id || '';
    const parts = label
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);
    const primary = parts[0] || label;

    let city = '';
    let country = '';
    if (parts.length >= 2) {
      city = parts[0];
      country = parts[parts.length - 1];
    } else {
      country = primary;
    }

    setLocation({
      option: place,
      label,
      primary,
      city: isBrand ? '' : city,
      country: isBrand ? primary : country,
      countryCode: '',
      placeId,
    });

    if (!placeId) return;
    try {
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?place_id=${placeId}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
      );
      const data = await res.json();
      const comps = data?.results?.[0]?.address_components || [];
      const countryComp = comps.find((c) => c.types.includes('country'));
      const cityComp =
        comps.find((c) => c.types.includes('locality')) ||
        comps.find((c) => c.types.includes('postal_town')) ||
        comps.find((c) => c.types.includes('administrative_area_level_3')) ||
        comps.find((c) => c.types.includes('administrative_area_level_2'));
      setLocation((prev) => ({
        ...prev,
        city: isBrand ? '' : cityComp?.long_name || prev.city,
        country: countryComp?.long_name || prev.country,
        countryCode: countryComp?.short_name || prev.countryCode,
      }));
    } catch (err) {
      console.error('Geocoding error:', err);
    }
  };

  const filtersCount = useMemo(() => {
    const f = selectedFilters;
    let n = (f.macroCategories?.length || 0) + (f.microCategories?.length || 0);
    if (!isBrand) {
      n += f.utilities?.length || 0;
      if (f.maxPrice != null && Number.isFinite(Number(f.maxPrice))) n += 1;
      if (f.hidePastEvents === false) n += 1;
      if (f.hideLiveEvents === true) n += 1;
      if (f.monthRange?.from || f.monthRange?.to) n += 1;
      if (selectedDates.from || selectedDates.to) n += 1;
    }
    if (location.label) n += 1;
    return n;
  }, [selectedFilters, location, selectedDates, isBrand]);

  const runSearch = () => {
    const params = new URLSearchParams();
    params.set('searchType', tab);
    const q = (query || '').trim();

    // Same q → category/utility id derivation as the listings EventSearchBar,
    // so a homepage search and a listing-page search send identical params.
    const { qMacroIds, qMicroIds, qUtilityIds } = getIdsFromQuery(q);
    const onlyUtilityMatch =
      !isBrand &&
      qUtilityIds.length > 0 &&
      qMacroIds.length === 0 &&
      qMicroIds.length === 0;
    const effectiveQ = onlyUtilityMatch ? '' : q;
    if (effectiveQ) params.set('q', effectiveQ);

    if (location.city) params.set('city', location.city);
    if (location.country) params.set('country', location.country);
    if (location.countryCode) params.set('countryCode', location.countryCode);
    if (location.label) params.set('locationLabel', location.label);

    const set = (key, val) => {
      if (Array.isArray(val)) {
        const list = val.map(String).filter(Boolean);
        if (list.length) params.set(key, list.join(','));
      } else if (val != null && String(val).trim()) {
        params.set(key, String(val));
      }
    };
    set('qMacroCategories', qMacroIds);
    set('qMicroCategories', qMicroIds);
    set('macroCategories', selectedFilters.macroCategories);
    set('microCategories', selectedFilters.microCategories);

    if (!isBrand) {
      set('utilities', [
        ...new Set([...(selectedFilters.utilities || []), ...qUtilityIds]),
      ]);
      if (
        selectedFilters.maxPrice != null &&
        Number.isFinite(Number(selectedFilters.maxPrice))
      )
        params.set('maxPrice', String(selectedFilters.maxPrice));
      if (selectedFilters.hidePastEvents === false)
        params.set('hidePastEvents', 'false');
      if (selectedFilters.hideLiveEvents === true)
        params.set('hideLiveEvents', 'true');
      if (selectedFilters.monthRange?.from)
        params.set('monthFrom', selectedFilters.monthRange.from);
      if (selectedFilters.monthRange?.to)
        params.set('monthTo', selectedFilters.monthRange.to);
      if (selectedDates.from)
        params.set('startDate', toYmd(selectedDates.from));
      if (selectedDates.to) params.set('endDate', toYmd(selectedDates.to));
    }

    navigate(`/listings?${params.toString()}`);
  };

  const onSubmit = (e) => {
    e?.preventDefault?.();
    setSuggestions([]);
    runSearch();
  };

  return (
    <div ref={wrapperRef} className="mt-5 lg:mt-9 max-w-[640px]">
      {/* Tabs */}
      <div className="inline-flex items-center gap-0.5 bg-white/70 backdrop-blur rounded-full p-0.5 ring-1 ring-line shadow-sm mb-3">
        {['events', 'brands'].map((t2) => (
          <button
            key={t2}
            type="button"
            onClick={() => setTab(t2)}
            className={cx(
              'px-4 py-1.5 rounded-full text-[12px] font-bold transition',
              tab === t2
                ? t2 === 'events'
                  ? 'bg-brand text-ink-strong shadow-sm'
                  : 'bg-ink-strong text-white shadow-sm'
                : 'text-gray-500 hover:text-ink-strong'
            )}
          >
            {t2 === 'events'
              ? t('homepage.tab_find_event')
              : t('homepage.tab_find_brand')}
          </button>
        ))}
      </div>

      <form onSubmit={onSubmit} className="relative">
        <div className="search-shell bg-white rounded-full ring-1 ring-line shadow-card p-1.5 flex items-center gap-1 transition">
          <div className="flex-1 flex items-center gap-2 px-4 min-w-0 relative">
            <MagnifyingGlassIcon className="h-[15px] w-[15px] text-gray-400 shrink-0" />
            <div className="relative flex-1 min-w-0">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder=""
                aria-label={basePlaceholder}
                className="w-full bg-transparent text-[16px] sm:text-[14px] text-ink-strong focus:outline-none min-w-0 py-2"
              />
              {!query && (
                <ScrollingPlaceholder
                  text={activePlaceholder}
                  items={Array.isArray(placeholderList) ? placeholderList : []}
                />
              )}
            </div>
            {query && (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  setSuggestions([]);
                }}
                className="shrink-0 p-1 rounded-full hover:bg-gray-100"
                aria-label={t('listing.clear')}
              >
                <XMarkIcon className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </div>

          {/* Filters */}
          <button
            type="button"
            onClick={() => {
              setSuggestions([]);
              setFiltersOpen((v) => !v);
            }}
            className={cx(
              'relative shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-full text-[13px] font-bold transition ring-1',
              filtersOpen || filtersCount > 0
                ? 'bg-ink-strong text-white ring-ink-strong'
                : 'bg-white text-ink-strong ring-line hover:ring-ink-strong'
            )}
            aria-label={t('listing.title')}
          >
            <AdjustmentsHorizontalIcon className="h-4 w-4" />
            <span className="hidden sm:inline">{t('listing.title')}</span>
            {filtersCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-brand text-ink-strong text-[11px] font-extrabold">
                {filtersCount}
              </span>
            )}
          </button>

          <button
            type="submit"
            aria-label={t('listing.search')}
            className={`shrink-0 p-2.5 sm:px-5 sm:py-2.5 rounded-full text-[13px] font-extrabold transition shadow-sm ${
              isBrand
                ? 'bg-ink-strong text-white hover:bg-black'
                : 'bg-brand text-ink-strong hover:bg-brand-hover'
            }`}
          >
            <MagnifyingGlassIcon className="h-5 w-5 sm:hidden" />
            <span className="hidden sm:inline">{t('listing.search')}</span>
          </button>
        </div>

        {/* Suggestions */}
        {suggestions.length > 0 && (
          <div className="absolute left-0 right-0 sm:right-auto sm:w-[28rem] mt-2 z-50 bg-white rounded-2xl ring-1 ring-line shadow-lift max-h-80 overflow-y-auto">
            {suggestions.map((sug, i) => (
              <button
                key={sug.id || `${sug.type}-${sug.name}-${i}`}
                type="button"
                onClick={() => {
                  justSelectedSuggestion.current = true;
                  setQuery(sug.name);
                  setSuggestions([]);
                }}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50 text-left"
              >
                <span className="flex items-center gap-2 min-w-0">
                  {sug.type === 'category' || sug.type === 'subcategory' ? (
                    <CategoryIcon
                      macro={sug.macroId}
                      microCat={sug.microId}
                      className="w-5 h-5 shrink-0"
                    />
                  ) : sug.type === 'utility' ? (
                    <AdjustmentsHorizontalIcon className="w-5 h-5 text-brand shrink-0" />
                  ) : (
                    <BuildingStorefrontIcon className="w-5 h-5 text-gray-400 shrink-0" />
                  )}
                  <span className="text-[14px] text-ink-strong truncate">
                    {sug.name}
                  </span>
                </span>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 shrink-0">
                  {sug.type === 'category'
                    ? t('listing.category')
                    : sug.type === 'subcategory'
                      ? t('listing.subcategory')
                      : sug.type === 'utility'
                        ? t('listing.utilities')
                        : t('common.brand')}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Filters panel — When & Where live here */}
        {filtersOpen && (
          <div className="absolute left-0 right-0 mt-2 z-[60] bg-white rounded-2xl ring-1 ring-line shadow-lift p-4 space-y-3">
            {/* Where */}
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500 mb-1.5">
                {t('listing.where')}
              </div>
              <div className="relative rounded-full ring-1 ring-line bg-white">
                <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">
                  <MapPinIcon className="h-5 w-5" />
                </span>
                <GooglePlacesAutocomplete
                  key={`where-${tab}`}
                  apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                  apiOptions={{
                    language: (i18n.language || 'en').split('-')[0],
                  }}
                  autocompletionRequest={{ types: ['(regions)'] }}
                  selectProps={{
                    value: location.option,
                    onChange: onPlaceChange,
                    placeholder: t('listing.where'),
                    noOptionsMessage: () => t('homepage.no_options'),
                    styles: gpaStyles,
                    menuShouldScrollIntoView: false,
                    filterOption: (opt) => {
                      const types = opt?.data?.value?.types || [];
                      return isBrand
                        ? isCountryPrediction(opt)
                        : isCityOrCountry(types);
                    },
                  }}
                />
                {location.label && (
                  <button
                    type="button"
                    onClick={() => setLocation(EMPTY_LOCATION)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full bg-gray-100 hover:bg-gray-200"
                    aria-label={t('listing.clear')}
                  >
                    <XMarkIcon className="w-4 h-4 text-gray-500" />
                  </button>
                )}
              </div>
            </div>

            {/* When (events only) */}
            {!isBrand && (
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500 mb-1.5">
                  {t('listing.when')}
                </div>
                <DatePicker
                  selectedDates={selectedDates}
                  onDateChange={setSelectedDates}
                  placeholder={t('listing.when')}
                />
              </div>
            )}

            {/* More filters + actions */}
            <div className="flex items-center justify-between pt-1">
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                className="inline-flex items-center gap-1.5 text-[13px] font-bold text-ink-strong hover:underline"
              >
                <AdjustmentsHorizontalIcon className="h-4 w-4" />
                {t('homepage.more_filters')}
                {filtersCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-brand text-ink-strong text-[11px] font-extrabold">
                    {filtersCount}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  setFiltersOpen(false);
                  onSubmit();
                }}
                className="px-4 py-2 rounded-full bg-ink-strong text-white text-[13px] font-bold hover:bg-black transition"
              >
                {t('listing.search')}
              </button>
            </div>
          </div>
        )}
      </form>

      <FiltersModal
        isOpen={modalOpen}
        isBrandSearch={isBrand}
        onClose={() => setModalOpen(false)}
        onApplyFilters={(f) =>
          setSelectedFilters((prev) => ({ ...prev, ...f }))
        }
        categories={categories || []}
        utilities={isBrand ? [] : UTILITIES}
        initialFilters={selectedFilters}
        isAuthenticated={false}
      />
    </div>
  );
}
